import { useEffect, useState } from 'react';
import { fetchNotrufplan, fetchProjektMetadaten } from '../../services/asplanService';
import { mitZeitlimit } from '../../services/ablageHilfen';
import type { ReportMetadata } from '../../types';
import type { NotrufplanInhalt } from '../sige-plan/types';

/**
 * Was das A+S-Modul aus dem uebrigen Projekt LIEST — Stammdaten und
 * Notrufplan.
 *
 * ═══ LESEN JA, SCHREIBEN NIE ══════════════════════════════════════════════
 * Dieser Hook laedt und sonst nichts. Jede Uebernahme in den Plan laeuft ueber
 * einen Knopf; eine Vorbelegung, die sich beim Laden selbst speichert,
 * mutiert Bestandsdatensaetze rueckwirkend (Lehre aus Handbuch und
 * Planungsphase, und der Grund, warum `fetchAsplanBundle` nichts anlegt).
 *
 * ═══ WARUM „FUER" IM ZUSTAND STEHT UND KEINE LAEDT-FLAGGE ═════════════════
 * Ein synchron im Effekt gesetztes `laedt: true` waere eine Kaskadenrenderung
 * — der Lint dieses Projekts verbietet sie. Steht das Projekt IM Zustand, ist
 * „noch nicht geladen" schlicht „gehoert zu einem anderen Projekt".
 *
 * ═══ WARUM BEIDE ZUSAMMEN — UND WARUM NUR EINMAL ══════════════════════════
 * Reiter „Grundlagen" (Notfallangaben), Reiter „Beteiligte" (Uebernahme) und
 * Reiter „Aushaenge" (Rettungskette) brauchen dieselben zwei Quellen. Zwei
 * Hooks laesen sie zweimal und koennten auseinanderlaufen; ein halb geladener
 * Satz zeigte auf dem einen Blatt einen Stand, den das andere noch nicht
 * kennt — bei NOTFALLNUMMERN.
 *
 * Deshalb wird dieser Hook GENAU EINMAL aufgerufen, in `AsplanModul.tsx`, und
 * das Ergebnis als Prop durchgereicht. Der duenne Container haelt den Zustand,
 * die Karten zeigen ihn an (Repo-Doktrin, dieselbe wie beim ProjectHub). Ein
 * zweiter Aufruf in einer Karte waere ein zweiter Ladeweg — und laedt bei
 * jedem Reiterwechsel neu.
 *
 * ═══ `bereit` IST TEIL DER AUSSAGE ════════════════════════════════════════
 * Wer nur `notrufplan` liest, bekommt vor der Antwort `null` — und `null`
 * heisst „es gibt keinen", nicht „noch nicht gelesen". Genau daraus entstand
 * der gelbe Kasten „keine Notfallstelle hinterlegt" bei Projekten mit
 * vollstaendig gepflegtem Notrufplan. Jede Anzeige, die aus dem Fehlen etwas
 * folgert, muss `bereit` abfragen.
 *
 * ═══ MIT ZEITLIMIT — `bereit` KOMMT IMMER ═════════════════════════════════
 * Jede Quelle liest mit `mitZeitlimit` und faellt bei Fehler oder Ablauf auf
 * `null` zurueck. Sonst bliebe „Stammdaten werden noch gelesen" bei
 * haengendem Netz fuer immer stehen — und die Freigabe mit ihm. Ohne
 * Stammdaten bildet der Kern dann keinen Anker und sagt es.
 */

/** Eine Quelle lesen: mit Zeitlimit, bei Fehler oder Ablauf `null`. */
const lese = <T>(versprechen: Promise<T>): Promise<T | null> =>
    mitZeitlimit(versprechen).catch(() => null);

interface Gelesen {
    fuer: string;
    metadata: Partial<ReportMetadata> | null;
    notrufplan: NotrufplanInhalt | null;
}

export interface ProjektBestand {
    /** Erst `true`, wenn die Daten zu GENAU diesem Projekt vorliegen. */
    bereit: boolean;
    metadata: Partial<ReportMetadata> | null;
    notrufplan: NotrufplanInhalt | null;
}

export const useProjektBestand = (projectId: string): ProjektBestand => {
    const [gelesen, setGelesen] = useState<Gelesen | null>(null);

    useEffect(() => {
        let gilt = true;
        void Promise.all([
            lese(fetchProjektMetadaten(projectId)),
            lese(fetchNotrufplan(projectId)),
        ]).then(([metadata, notrufplan]) => {
            // Beide Lesewege sind fail-open: ein Projekt ohne SiGe-Plan ist
            // kein Fehlerfall, sondern der Normalfall am ersten Tag.
            if (gilt) setGelesen({ fuer: projectId, metadata, notrufplan });
        });
        return () => { gilt = false; };
    }, [projectId]);

    const bereit = gelesen !== null && gelesen.fuer === projectId;
    return {
        bereit,
        metadata: bereit && gelesen ? gelesen.metadata : null,
        notrufplan: bereit && gelesen ? gelesen.notrufplan : null,
    };
};
